<template>
  <view class="c-page" :class="{ 'overflow-inherit': slots.sticky }" style="background: #fafafa; min-height: 100vh;" :style="pageStyle ?? {}">
    <!-- <template> -->
    <template v-if="hasNavHeader">
      <nav-header
        v-if="!!slots.navHeaderLeftSolt"
        v-bind="{ ...navHeaderOptions }"
        @back="handleReturnBack"
        @rect="handleNavbarRectChange"
      >
        <template #leftSolt>
          <slot name="navHeaderLeftSolt" />
        </template>
      </nav-header>

      <nav-header
        v-else
        v-bind="{ ...navHeaderOptions }"
        @back="handleReturnBack"
        @rect="handleNavbarRectChange"
      />
    </template>

    <template v-if="slots.sticky">
      <view class="sticky-wrap" :style="{ top: stickyTopRef + 'px' }">
        <slot name="sticky" />
      </view>
    </template>

    <slot />

    <template v-if="hasFooter">
      <view class="footer" :style="footerOptions?.buttonOuterStyle ?? {}">
        <slot name="footerButtonTopSolt" />
        <button-group :itemList="footerOptions?.buttonGroupConfig ?? []" class="button-group" />
      </view>
    </template>
    <!-- </template> -->

    <!-- 各页面独立的加载组件，防止全局效果时，手势返回等掩盖页面布局问题 -->
    <app-loading-provider v-if="pageLoading" />

    <resource-previewer />

    <toast-overlay />

    <abnormal-prompt-popup />

    <float-tools
      :share-earn-options="shareEarnOptions"
      :show-customer-service="floatToolsOptions?.showCustomerService"
      :show-shopping-cart="floatToolsOptions?.showShoppingCart"
      :position="floatToolsOptions?.position"
    />
  </view>
</template>

<script setup lang="ts">
import AbnormalPromptPopup from '@/components/abnormal-prompt-popup/index.vue';
import AppLoadingProvider from '@/components/app-loading-provider/index.vue';
import ButtonGroup from '@/components/button-group/index.vue';
import FloatTools from '@/components/float-tools/index.vue';
import NavHeader from '@/components/nav-header/index.vue';
import ResourcePreviewer from '@/components/resource-previewer/index.vue';
import ToastOverlay from '@/components/toast/index.vue';
import { PAGE_PATH } from '@/constants';
import { useAppStore } from '@/store';
import type { AnyObj, Themes } from '@/types';
import type { Comp } from '@/types/global-business';
import { toast } from '@/utils';
import errorManager from '@/utils/errorManager';
import { onShow, onUnload } from '@dcloudio/uni-app';
import { defineEmits, ref, toRefs, useSlots, watch } from 'vue';

interface Props {
  /** 页面样式对象 */
  pageStyle?: AnyObj;
  /** 是否需要页面加载效果 */
  pageLoading?: boolean;
  /** 是否需要页面顶部导航栏 */
  hasNavHeader?: boolean;
  /** 分享赚数据 */
  shareEarnOptions?: {
    shareValue: string;
    hidden?: boolean;
  };
  floatToolsOptions?: {
    showCustomerService?: boolean;
    showShoppingCart?: boolean;
    position?: {
      bottom?: number;
      right?: number;
    };
  };
  /** 头部导航选项 */
  navHeaderOptions?: {
    /** 标题 */
    title?: string;
    /** 是否展示页面标题 */
    displayTitle?: boolean;
    /** 是否展示返回按钮 */
    displayReturnBack?: boolean;
    /** 是否展示客服按钮 */
    displayCustomerService?: boolean;
    /** 是否固定顶部 */
    fixed?: boolean;
    /** 自定义返回事件，需传入相关事件 */
    customBack?: boolean;
    /** 背景颜色 */
    backgroundColor?: string;
    /** 描述格调风格颜色，影响标题栏文本颜色和返回按钮的颜色 */
    styleTheme?: Themes;
    /** 是否展示阴影 */
    shadow?: boolean;
    /** 是否自动填充fixed定位下的空缺 */
    autoFill?: boolean;
    /** 最外层样式 */
    navOuterStyle?: AnyObject;
  };
  /** 是否需要页面底部按钮 - fixed */
  hasFooter?: boolean;
  /** 底部按钮选项 */
  footerOptions?: {
    /** 页面底部按钮样式 */
    buttonOuterStyle?: AnyObj;
    /** 页面底部按钮配置 - hasFooter为true的时候必传 */
    buttonGroupConfig: Comp.ButtonGroupItem[];
    /** 是否透明底部背景色 */
    transparentBackground?: boolean;
  };
}

const props = withDefaults(defineProps<Props>(), {
  hasNavHeader: true,
  hasFooter: false,
  pageLoading: false,
  shareEarnOptions: () => ({
    shareValue: '',
    display: true,
  }),
  buttonGroupConfig: () => [],
});

const {
  pageStyle,
  pageLoading,
  hasNavHeader,
  hasFooter,
  navHeaderOptions,
  footerOptions,
  shareEarnOptions,
  floatToolsOptions,
} = toRefs(props);

const emits = defineEmits(['back', 'navbarRect']);
// const instance = getCurrentInstance();

const slots = useSlots();

const appStore = useAppStore();

let _pages: Page.PageInstance<AnyObject, {}>[] | null = null;

const stickyTopRef = ref(0);

const handleNavbarRectChange = (rect: AnyObject) => {
  emits('navbarRect', rect);
  stickyTopRef.value = rect?.height ?? 0;
};

const handleReturnBack = () => {
  emits('back');
};

/** 监听网络慢、异常，针对匹配的页面添加modal提示 */
watch(
  () => appStore.networkErrorFlag,
  (flag) => {
    if (flag) {
      if (!_pages) _pages = getCurrentPages();
      const currentPage = _pages[_pages.length - 1].route;
      // 匹配页面
      const isMatch = [
        PAGE_PATH.home.route,
        PAGE_PATH.commodity.route,
        PAGE_PATH.cart.route,
        PAGE_PATH.self.route,
        PAGE_PATH.memberDetail.route,
      ].includes(`/${currentPage}`);
      if (isMatch) {
        uni.showModal({
          title: '当前网络差',
          content: '是否尝试重新加载页面',
          confirmText: '好的',
          success: (res) => {
            if (res.confirm) {
              uni.reLaunch({ url: `/${currentPage}` });
            }
          },
          complete: () => {
            appStore.networkErrorFlag = false;
          },
        });
      }
    }
  }
);

onShow(async () => {
  errorManager.collectOperatePath();
});

onUnload(() => {
  // 清除所有未完成的toast任务
  toast.clear();
});
</script>

<style scoped lang="scss">
.overflow-inherit {
  overflow: inherit !important;
}

.inner {
  visibility: hidden;

  &.visible {
    visibility: visible;
  }
}

.sticky-wrap {
  position: sticky;
  top: 0px;
  z-index: 991;
  width: 100%;
}

.footer {
  @include topShadow;
  @include fixedFooter;
  z-index: 9;
  display: flex;
  align-items: center;
  flex-direction: columns;
  width: 100%;
  background: $W;
  padding-top: 10rpx;

  .button-group {
    flex: 1;
    font-weight: bold;
  }
}
</style>
